import { useEffect, useRef, useState } from "react";
import { Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IBoletoDetalhadoRetorno,
  IGerarBoletosCobrancaRetorno,
  IResumoFinanceiroCobrancaRetorno,
} from "@/models/consignado/proposal-billings.model";
import {
  buscarResumoFinanceiroCobranca,
  cancelarBoletoCobranca,
} from "@/services/consignado/proposta-cobranca.service";
import CollapsibleSection from "./CollapsibleSection";
import ResumoFinanceiro from "./ResumoFinanceiro";
import ParcelasTable from "./ParcelasTable";
import { getApiErrorMessage, useApiErrorToast } from "@/hooks/useApiErrorToast";

type Props = {
  numeroCcb: string;
};

// Seções "Resumo Financeiro" e "Parcelas" do detalhe da CCB — uma query só
// (GET /proposta/cobrancas/:ccb/resumo-financeiro devolve os agregados +
// `parcelas`). Na 1ª chamada o backend dispara o sync com a BMP, então pode
// demorar um pouco; enquanto isso as duas seções mostram o mesmo estado.
// Resumo Financeiro é a única seção do detalhe que nasce aberta (é ela que
// dispara a query); Parcelas nasce colapsada e só monta a tabela na 1ª abertura.
//
// Gerar / cancelar boleto: REAL (TAR-045, backend his-adm-cobranca-020
// `8fbfa26c`). O modal GerarBoletoModal faz o POST; aqui só se reage ao
// sucesso: toast com a mensagem do backend, invalida a query e agenda UM
// refetch pra `disponivel_em_segundos` (a geração não devolve o boleto — ele
// entra em `parcelas[].boletos[]` no próximo GET do resumo: o backend sincroniza
// com a BMP enquanto houver boleto gerado que ainda não pousou, `0c0c27b2`).
// Enquanto isso a parcela mostra "Boleto em processamento" pra evitar gerar
// duas vezes (o backend recusaria com 409 COBRANCA_ATIVA de qualquer forma).
// Cancelar: o backend já sincroniza na hora (`eed5fd00`), então o invalidate do
// onSuccess traz o boleto com `cancelado: true`.
export default function ResumoFinanceiroSection({ numeroCcb }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery<IResumoFinanceiroCobrancaRetorno>({
    queryKey: ["resumo-financeiro-cobranca", numeroCcb],
    queryFn: () => buscarResumoFinanceiroCobranca(numeroCcb),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const parcelas = data?.parcelas ?? [];
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<number[]>([]);
  const [parcelasAguardando, setParcelasAguardando] = useState<number[]>([]);
  const [parcelasAberta, setParcelasAberta] = useState(false);
  const refetchAgendado = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setParcelasSelecionadas([]);
  }, [data]);

  useEffect(
    () => () => {
      if (refetchAgendado.current) clearTimeout(refetchAgendado.current);
    },
    []
  );

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: ["resumo-financeiro-cobranca", numeroCcb] });

  const onBoletosGerados = (retorno: IGerarBoletosCobrancaRetorno) => {
    toast({
      title: "Cobrança solicitada",
      description: retorno.mensagem,
      status: "success",
      duration: 6000,
      isClosable: true,
    });
    setParcelasSelecionadas([]);
    setParcelasAguardando((prev) => [...new Set([...prev, ...retorno.cobrancas.flatMap((c) => c.parcelas)])]);
    invalidar();
    if (refetchAgendado.current) clearTimeout(refetchAgendado.current);
    refetchAgendado.current = setTimeout(invalidar, Math.max(1, retorno.disponivel_em_segundos) * 1000);
  };

  const cancelarBoleto = useMutation({
    mutationFn: (boleto: IBoletoDetalhadoRetorno) =>
      cancelarBoletoCobranca(numeroCcb, boleto.codigoCobranca),
    onSuccess(retorno) {
      toast({
        title: "Boleto cancelado",
        description: retorno.mensagem,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      invalidar();
    },
    onError: toastErro("Erro ao cancelar boleto"),
  });

  const toggleParcela = (numero: number) =>
    setParcelasSelecionadas((prev) =>
      prev.includes(numero) ? prev.filter((n) => n !== numero) : [...prev, numero]
    );

  // Quando o boleto chega no resumo, a parcela sai do "em processamento".
  // Cancelado não conta: se o operador gerou de novo, o que se espera é o novo.
  const parcelasComBoleto = new Set(
    parcelas.filter((p) => p.boletos.some((b) => !b.cancelado)).map((p) => p.numero_parcela)
  );
  const aguardandoVisiveis = parcelasAguardando.filter((n) => !parcelasComBoleto.has(n));

  const estado = isLoading ? (
    <Flex justifyContent="center" py={4}>
      <Spinner color="primary" />
    </Flex>
  ) : isError || !data ? (
    <Text fontSize="sm" color="gray.500">
      Não foi possível carregar o resumo financeiro desta CCB.
      {isError && ` ${getApiErrorMessage(error, "")}`}
    </Text>
  ) : null;

  return (
    <>
      <CollapsibleSection title="Resumo Financeiro">
        {estado ?? <ResumoFinanceiro resumo={data!} />}
      </CollapsibleSection>

      <CollapsibleSection
        title="Parcelas"
        defaultExpanded={false}
        onToggle={(expanded) => expanded && setParcelasAberta(true)}
      >
        {parcelasAberta &&
          (estado ?? (
            <ParcelasTable
              numeroCcb={numeroCcb}
              parcelas={parcelas}
              parcelasSelecionadas={parcelasSelecionadas}
              onToggleParcela={toggleParcela}
              onBoletosGerados={onBoletosGerados}
              parcelasAguardandoBoleto={aguardandoVisiveis}
              onCancelarBoleto={(boleto) => cancelarBoleto.mutateAsync(boleto)}
              isCancelandoBoleto={cancelarBoleto.isPending}
            />
          ))}
      </CollapsibleSection>
    </>
  );
}
